/**
 * @packageDocumentation
 * Defines the main card screen including flip logic, wild morphing, sequential reveals, win handling, and integration with popups and HUD elements.
 */
import * as PIXI from "pixi.js";
import { gsap } from "gsap";
// Use the spine runtime that matches your export from Esoteric Spine
// import { Spine } from "@pixi/spine-4.1";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import {
  currencySymbol,
  getRoundMock,
  lastTransformTargets,
  ShowCent,
  WILD_CIVIL_ID,
  WILD_SUPER_ID,
  ScenarioResult,
} from "./server";
import { specFromId, specsFromRoundTuple } from "./loadCards";
import { canvasProperties } from "../config";
import { Assets } from "pixi.js";
import { LogoAnim } from "../app";
import { BottomBar } from "./bottomBar";
import { dullFilter, dullFilter2 } from "./global";
import { SoundManager } from "./sounds";

const WIN_SCENARIO_ORDER: ScenarioResult["scenario"][] = ["sameBorder", "sameCharacter", "allSuper"];
const POPUP_HIDE_FALLBACK_MS = 3600;

/**
 * Global flip timing knobs. Adjust the multiplier to speed up/slow down every card flip
 * while keeping authored option overrides intact.
 */
const FLIP_SPEED_MULTIPLIER = 1.2;
const DEFAULT_HALF_DURATION_MS = Math.round(220 / FLIP_SPEED_MULTIPLIER);
const DEFAULT_REVEAL_STAGGER_MS = Math.round(200 / FLIP_SPEED_MULTIPLIER);
const DEFAULT_RESET_STAGGER_MS = Math.round(180 / FLIP_SPEED_MULTIPLIER);


function shouldEnableSpinButton(): boolean {
  const autoplay = (globalThis as any)?.autoplay;
  return !autoplay?.isRunningNow?.();
}

/**
 * Public API for configuring flip timing & feel
 */
export type CardFlipOptions = {
  /** milliseconds for each half-flip; defaults to DEFAULT_HALF_DURATION_MS (~2x faster total flip) */
  halfDuration?: number; // default DEFAULT_HALF_DURATION_MS (~146ms)
  /** delay between flipping cards; defaults to DEFAULT_RESET_STAGGER_MS */
  stagger?: number; // default DEFAULT_RESET_STAGGER_MS (~120ms)
  /** little z-tilt to sell the 3D feel (degrees) */
  tiltDeg?: number; // default 8
  /** how much to overshoot scaleX when finishing (1=none, 1.06=gentle punch) */
  finishPunch?: number; // default 1.05
  /** easing used for both halves */
  ease?: string; // default "power2.inOut"
  nearTowin?: boolean,
};

export type SymbolSpec = {
  /** key for PIXI.Texture to show as the CLOSED/back image */
  backTexture: PIXI.Texture;
  /** FRONT (revealed) content can be either Spine or a Sprite texture */
  // Spine front (preferred)
  spineJsonKey?: string; // loader key used by your assets loader
  spineAtlasKey?: string; // loader key used by your assets loader
  /** optional name of animation to play once revealed */
  anim?: string;
  /** optional scale & offset to fit card rect */
  spineScale?: number;
  spineX?: number;
  spineY?: number;

  // Or, provide a static sprite front (e.g., Wildcard placeholder)
  frontSpriteTexture?: PIXI.Texture;
  frontScale?: number;
  frontY?: number;

  /** Optional animated frame overlay (Spine) shown only when revealed */
  frameJsonKey?: string;
  frameAtlasKey?: string;
  frameAnim?: string; // e.g., "animation" / "idle"
  frameScale?: number;
  frameY?: number;
};

export class CardContainer extends PIXI.Container {
  spinStarted: boolean = false;
  BMS_animation: Spine | undefined;
  BMS_win_triggered: boolean | undefined;
  isThereMingame: any;
  totalWin: number = 0;

  cardContainer!: PIXI.Container;
  EndPage: any;
  showEndpage: (() => void) | undefined;
  playLogoAnim: (() => void) | undefined;
  app: PIXI.Application<PIXI.Renderer> | undefined;
  screen!: CardScreen;

  constructor(app: PIXI.Application) {
    super();

    // IMPORTANT: the card visuals must live under THIS container
    // so app.ts can put CardContainer under designRoot and scale/position it correctly.
    const cardContainer = new PIXI.Container();
    this.cardContainer = cardContainer;
    this.addChild(cardContainer);

    // start async build (no need to await here)
    void this.createContainer(cardContainer);
  }

  async createContainer(cardContainer: PIXI.Container) {
    this.screen = await onPreloadStartGame(cardContainer);

    this.screen.scale.set(1);
    this.screen.position.set(0, 0);

    // ✅ Freeze fitting bounds so open/front Spine cannot inflate them later
    const b = this.screen.getLocalBounds();
    cardContainer.boundsArea = new PIXI.Rectangle(b.x, b.y, b.width, b.height);
  }

}


/**
 * A single flip card with a static back image, a front (Spine or Sprite),
 * and an optional animated frame overlay (Spine) that appears when revealed.
 *
 * Positions & scales were kept compatible with the first version of this file.
 */
export class FlipCard extends PIXI.Container {
  private back: PIXI.Sprite;
  private frontSpine: Spine | null = null;
  private frontSprite: PIXI.Sprite | null = null;
  private frameOverlay: Spine | null = null;
  private _isOpen = false;
  private _w = 270; // logical width of card placeholder (10% smaller)
  private _h = 405; // logical height of card placeholder (10% smaller)
  private _cornerRadius = 0;
  // public LogoAnim!:Spine;

  constructor(public spec: SymbolSpec, w?: number, h?: number) {
    super();
    if (w) this._w = w;
    if (h) this._h = h;

    // BACK (closed) side
    this.back = new PIXI.Sprite(spec.backTexture);
    this.back.anchor.set(0.5);
    this.back.width = this._w;
    this.back.height = this._h;

    // Card outline / click target
    const frame = new PIXI.Graphics();
    frame.roundRect(
      -this._w / 2,
      -this._h / 2,
      this._w,
      this._h,
      this._cornerRadius
    );
    frame.stroke({ width: 4, color: 0x000000, alpha: 0.25 });

    // mask to keep rounded-corner look for both sides
    const maskG = new PIXI.Graphics();
    maskG.roundRect(
      -this._w / 2,
      -this._h / 2,
      this._w,
      this._h,
      this._cornerRadius
    );
    maskG.fill(0xffffff);

    this.addChild(this.back);
    // this.addChild(maskG);
    // this.mask = maskG;

    // Prepare front + optional frame (but keep hidden until reveal)
    this.createFront(spec);
    this.createFrame(spec);

    // subtle idle hover
    this.interactive = false;
    this.on("pointerover", () =>
      gsap.to(this.scale, { x: 1.03, y: 1.03, duration: 0.15 })
    );
    this.on("pointerout", () =>
      gsap.to(this.scale, { x: 1.0, y: 1.0, duration: 0.2 })
    );
  }

  private createFront(spec: SymbolSpec) {
    // Spine front
    if (spec.spineJsonKey && spec.spineAtlasKey) {
      console.log("testig spec", spec);
      console.log("testig spec", spec.spineJsonKey);
      console.log("testig spec", spec.spineAtlasKey);

      // const data = Assets.get(spec.spineJsonKey); // MUST be loaded already
      // const s = new Spine(data);
      const s = Spine.from({
        skeleton: spec.spineJsonKey,
        atlas: spec.spineAtlasKey,
        scale: 1,
      });

      s.scale.set(spec.spineScale ?? 0.45); // 10% smaller: 0.5 * 0.9
      s.x = spec.spineX ?? 0;
      s.y = spec.spineY ?? this._h * 0.5;
      s.visible = false;
      this.addChild(s);
      this.frontSpine = s;

      if (spec.anim) {
        s.state.setAnimation(0, spec.anim, true);
        s.state.timeScale = 1.0;
      }
      return;
    }

    // Static front (Wildcard fallback)
    if (spec.frontSpriteTexture) {
      const spr = new PIXI.Sprite(spec.frontSpriteTexture);
      spr.anchor.set(0.5);
      spr.scale.set(spec.frontScale ?? 0.9); // 10% smaller: 1.0 * 0.9
      spr.y = spec.frontY ?? 0;
      spr.visible = false;
      this.addChild(spr);
      this.frontSprite = spr;
    }
  }

  private createFrame(spec: SymbolSpec) {
    if (!(spec.frameJsonKey && spec.frameAtlasKey)) return;
    console.log("testing frame spec", spec);
    console.log("testing frame spec", spec.frameJsonKey);
    console.log("testing frame spec", spec.frameAtlasKey);
    const f = Spine.from({
      skeleton: spec.frameJsonKey,
      atlas: spec.frameAtlasKey,
      scale: 1,
    });

    // const fData = Assets.get(spec.frameJsonKey); // MUST be loaded
    // const f = new Spine(fData);
    f.scale.set(spec.frameScale ?? 0.9); // 10% smaller: 1.0 * 0.9
    f.x = 0;
    f.y = spec.frameY ?? 0;
    f.visible = false; // only visible when open
    this.addChild(f);
    this.frameOverlay = f;
    let frameAnim = "static";

    if (frameAnim) {
      f.state.setAnimation(0, frameAnim, true);
      f.state.timeScale = 1.0;
    }
  }

  get isOpen() {
    return this._isOpen;
  }

  /** Instantly show back (closed) */
  public resetClosed() {
    if (this.frontSpine) this.frontSpine.visible = false;
    if (this.frontSprite) this.frontSprite.visible = false;
    if (this.frameOverlay) this.frameOverlay.visible = false;
    this.back.visible = true;
    this.scale.x = 1;
    this._isOpen = false;
  }

  /** Instantly show front (open) */
  public setOpenInstant() {
    if (this.frontSpine) this.frontSpine.visible = true;
    if (this.frontSprite) this.frontSprite.visible = true;
    if (this.frameOverlay) this.frameOverlay.visible = true;
    this.back.visible = false;
    this.scale.x = 1;
    this._isOpen = true;
  }
  public async morphToId(
    targetId: number,
    opts?: { half?: number; finishPunch?: number; ease?: string }
  ) {
    const half = opts?.half ?? 0.2;
    const ease = opts?.ease ?? "power2.inOut";
    const punch = opts?.finishPunch ?? 1.06;
    SoundManager.Wild_Card_Flip.play();
    return new Promise<void>((res) => {
      const tl = gsap.timeline({ onComplete: () => res() });
      const startSkewY = this.skew.y;

      // edge
      tl.to(this.scale, { x: 0.0, duration: half, ease }, 0)
      // .to(this.skew,  { y: startSkewY + 0.15, duration: half, ease }, 0);

      // swap
      tl.add(() => {
        this.applySpec(specFromId(targetId)); // resets closed; we force-open front
        if (this.frontSpine) this.frontSpine.visible = true;
        if (this.frontSprite) this.frontSprite.visible = true;
        if (this.frameOverlay) this.frameOverlay.visible = true;
        this.back.visible = false;
        (this as any)._isOpen = true;
        this.scale.x = 0.0;
      });

      // open + settle
      tl.to(this.scale, { x: punch, duration: half, ease })
        .to(this.scale, { x: 1.0, duration: 0.1, ease: "power3.out" })
      // .to(this.skew,  { y: startSkewY, duration: half, ease }, `>-=${half}`);
    });
  }

  /**
   * Plays a flip animation. Resolves when the flip is finished.
   */
  public async flip(options: CardFlipOptions = {}): Promise<void> {
    const ease = options.ease ?? "power2.inOut";
    const tilt = (options.tiltDeg ?? 8) * (Math.PI / 180);
    const punch = options.finishPunch ?? 1.05;
    const nearTowinDelay = options.nearTowin ? 3 : 1;
    const half = ((options.halfDuration ?? DEFAULT_HALF_DURATION_MS) / 1000) * nearTowinDelay;
    if (options.nearTowin) {
      SoundManager.Card_Slow_Flip.play();
    } else {
      if (SoundManager.Card_Normal_Flip.playing()) {
        SoundManager.Card_Normal_Flip.stop();
      }
      SoundManager.Card_Normal_Flip.play();
    }


    this.emit("flipstart", { open: !this._isOpen });

    // little Y-rotation illusion by skewing; Pixi v8 has skew object
    const startSkewY = this.skew.y;

    await new Promise<void>((resolve) => {
      const tl = gsap.timeline({ onComplete: resolve });
      // half 1: go to zero width (hide current side)
      tl.to(this.scale, { x: 0.0, duration: half, ease }, 0);
      // tl.to(this.skew, { y: startSkewY + tilt, duration: half, ease }, 0);

      // swap side at midpoint
      tl.add(() => {
        if (this._isOpen) {
          // currently front visible; go to back
          if (this.frontSpine) this.frontSpine.visible = false;
          if (this.frontSprite) this.frontSprite.visible = false;
          if (this.frameOverlay) this.frameOverlay.visible = false;
          this.back.visible = true;
          this._isOpen = false;
        } else {
          if (this.frontSpine) this.frontSpine.visible = true;
          if (this.frontSprite) this.frontSprite.visible = true;
          if (this.frameOverlay) this.frameOverlay.visible = true; // show frame only when open
          this.back.visible = false;
          this._isOpen = true;
        }
      });
      // half 2: expand to full width again with a tiny punch
      tl.to(this.scale, { x: punch, y: punch, duration: half, ease });
      tl.to(this.scale, { x: 1.0, y: 1.0, duration: 0.1, ease: "power3.out" });
      // tl.to(this.skew, { y: startSkewY, duration: half, ease }, `>-=${half}`);
    });

    this.emit("flipend", { open: this._isOpen });
  }

  public applySpec(spec: SymbolSpec) {
    this.spec = spec;

    // remove old front + frame
    if (this.frontSpine) {
      this.removeChild(this.frontSpine);
      this.frontSpine.destroy({ children: true });
      this.frontSpine = null;
    }
    if (this.frontSprite) {
      this.removeChild(this.frontSprite);
      this.frontSprite.destroy();
      this.frontSprite = null;
    }
    if (this.frameOverlay) {
      this.removeChild(this.frameOverlay);
      this.frameOverlay.destroy({ children: true });
      this.frameOverlay = null;
    }

    // recreate with the same geometry
    this.createFront(spec);
    this.createFrame(spec);
    this.resetClosed(); // ensure we're back-facing after swap
  }
  // inside class FlipCard
  public playWinAnim(winAmount: number) {
    if (!this.frontSpine) return;
    const idle = this.spec?.anim ?? "idle_anim";
    try {
      // play once, then go back to idle
      try {
        this.frontSpine.state.setAnimation(0, "win_anim", false);
        this.frontSpine.state.addAnimation(0, idle, true, 0);
      } catch (error) { }

      if (this.frameOverlay) {
        this.frameOverlay.state.setAnimation(0, "animation", false);
        this.frameOverlay.state.addAnimation(0, "animation", false);
        this.frameOverlay.state.addAnimation(0, "static", true, 0);
      }
      LogoAnim.state.setAnimation(0, "win_anim", true);
      setTimeout(() => {
        LogoAnim.winPopup!.showWinpopup(winAmount);
      }, 2000);
    } catch (e) {
      console.warn("win_anim not found on this spine:", e);
    }
  }
}

/** Layout + sequencing container for 3 cards */
export class CardScreen extends PIXI.Container {
  public cards: FlipCard[] = [];
  private spacing = 15;
  // public LogoAnim!:Spine
  constructor(specs: SymbolSpec[], cardSize = { w: 300, h: 450 }) {
    super();
    if (specs.length !== 3) {
      console.warn(
        "CardScreen expects exactly 3 specs; extra will be ignored / missing will be placeholders"
      );
    }

    // build 3 cards
    for (let i = 0; i < 3; i++) {
      const spec = specs[i] ?? this.makePlaceholderSpec();
      const c = new FlipCard(spec, cardSize.w, cardSize.h);
      // c.LogoAnim = this.LogoAnim
      this.cards.push(c);
      this.addChild(c);
    }

    this.layout();
    this.sortableChildren = true;
    return this;
  }

  private makePlaceholderSpec(): SymbolSpec {
    const g = new PIXI.Graphics();
    g.roundRect(-180, -280, 360, 560, 36)
      .fill(0x222222)
      .stroke({ width: 4, color: 0x444444 });
    const tx = PIXI.RenderTexture.create({ width: 300, height: 450 });
    const tmp = new PIXI.Container();
    tmp.addChild(g);
    const renderer =
      (PIXI as any).renderer || (PIXI.Application as any).shared?.renderer;
    if (renderer) renderer.render(tmp, { renderTexture: tx });
    return {
      backTexture: tx,
      frontSpriteTexture: tx,
    };
  }

  public layout() {
    // horizontally center 3 cards (keeps original spacing & sizes)
    const totalW =
      this.cards.reduce((acc, c) => acc + (c as any)._w, 0) + this.spacing * 2;
    let x = -totalW / 2 + (this.cards[0] as any)._w / 2;
    for (let i = 0; i < this.cards.length; i++) {
      const c = this.cards[i];
      c.x = x;
      c.y = 0;
      x += (c as any)._w + this.spacing;
      c.zIndex = i; // simple overlap order
    }
  }

  /** resets all to closed/back side */
  public resetAll() {
    this.cards.forEach((c) => c.resetClosed());
  }

  /**
   * Flip all cards one-by-one. Provide CardFlipOptions for timing behavior.
   * Returns a promise that resolves when all flips are done.
   */
  public async revealSequential(options: CardFlipOptions = {}): Promise<void> {
    const stagger = (options.stagger ?? DEFAULT_RESET_STAGGER_MS) / 1000;
    const newOptions = { ...options };
    for (let i = 0; i < this.cards.length; i++) {

      if (options.nearTowin && i == this.cards.length - 1) {
        newOptions.nearTowin = true
      } else {
        newOptions.nearTowin = false
      }

      await this.cards[i].flip(newOptions);
      if (i < this.cards.length - 1)
        await new Promise((r) => setTimeout(r, stagger * 1000));
    }
    this.emit("allflipsdone");
  }
  /**
   * Flip every open card back to its hidden state concurrently.
   * Any timing overrides supplied via CardFlipOptions apply to all flips.
   */
  public async flipAllToBack(options: CardFlipOptions = {}) {
    const openCards = this.cards.filter((card) => card.isOpen);
    if (!openCards.length) return;

    const baseOptions = { ...options, nearTowin: false };
    await Promise.all(openCards.map((card) => card.flip({ ...baseOptions })));
  }

  public setFronts(newSpecs: SymbolSpec[]) {
    for (let i = 0; i < this.cards.length; i++) {
      this.cards[i].applySpec(newSpecs[i]);
    }
  }

  public async nextRound(
    newSpecs: SymbolSpec[],
    options: CardFlipOptions = {}
  ) {
    await this.flipAllToBack({ ...options }); // no-op if already closed (first spin)
    this.setFronts(newSpecs); // swap content while closed
    await this.revealSequential(options); // reveal one by one
  }
  public async playWinSequence(results: ScenarioResult[]): Promise<void> {
    if (!results?.length) return;

    const ordered = WIN_SCENARIO_ORDER
      .map((scenario) => results.find((r) => r.scenario === scenario))
      .filter((r): r is ScenarioResult => !!r);

    if (!ordered.length) return;

    LogoAnim.StartBut_disableFun?.();
    LogoAnim.bottomWin && (LogoAnim.bottomWin.visible = true);

    let cumulative = 0;

    try {
      for (let i = 0; i < ordered.length; i++) {
        const entry = ordered[i];
        cumulative += entry.amount;
        await this.playScenarioWin(entry, cumulative, i === ordered.length - 1);
      }
    } finally {
      if (LogoAnim.bottomWin) {
        LogoAnim.bottomWin.visible = false;
      }
    };
    const popup = LogoAnim.winPopup;
    if (popup) {
      this.cards.forEach((c) => {
        (c as any).frontSpine.filters = [];
      });
      popup.showWinpopup(cumulative);
      await this.waitForPopupHidden();
    } else {
      await this.delay(POPUP_HIDE_FALLBACK_MS);
      if (shouldEnableSpinButton()) {
        LogoAnim.StartBut_enableFun?.();
      }
    }
  }

  private async playScenarioWin(result: ScenarioResult, _cumulative: number, isLast: boolean) {
    if (result.scenario === "sameBorder") {
      this.playBorderWinAnim();
    } else {
      const spec = this.cards[0]?.spec;
      if (spec) {
        if (result.scenario == "allSuper") {
          SoundManager.Heroes_Character_Win.play();
        } else {
          SoundManager.Civilian_Character_Win.play();
        }
        this.playCharacterWinAnim(spec);
      } else {
        this.playBorderWinAnim();
      }
    }

    if (LogoAnim.state) {
      try {
        LogoAnim.state.setAnimation(0, "win_anim", true);
      } catch (err) {
        console.warn("LogoAnim win_anim failed:", err);
      }
    }

    if (LogoAnim.bottomWin) {
      LogoAnim.bottomWin.visible = true;
      LogoAnim.bottomWin.updateForScenario(result);
      LogoAnim.allsuper = result.scenario == "allSuper";
      console.log(result, "result")
    }

    await this.delay(isLast ? 2800 : 2650);
  }

  private playBorderWinAnim() {
    // (c as any).frontSpine.state.addAnimation(0, idle, true, 0);
    SoundManager.Border_win.play();
    this.cards.forEach((c) => {
      (c as any).frontSpine.filters = [dullFilter2];
      (c as any).frameOverlay.state.setAnimation(0, "animation", false);
      (c as any).frameOverlay.state.addAnimation(0, "animation", false);
      (c as any).frameOverlay.state.addAnimation(0, "static", true, 0);
    });
  }
  private playCharacterWinAnim(spec: SymbolSpec) {
    const idle = spec?.anim ?? "idle_anim";
    SoundManager.Border_win.stop();
    SoundManager.Civilian_Character_Win.play();
    this.cards.forEach((c) => {
      (c as any).frontSpine.filters = [];
      (c as any).frontSpine.state.setAnimation(0, "win_anim", false);
      (c as any).frontSpine.state.addAnimation(0, idle, true, 0);
    });
  }

  private delay(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
  }

  private async waitForPopupHidden(): Promise<void> {
    const popup = LogoAnim.winPopup;
    if (!popup) {
      await this.delay(POPUP_HIDE_FALLBACK_MS);
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout>;
      const onHidden = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        popup.off?.("winpopup:hidden", onHidden);
        resolve();
      };
      popup.once?.("winpopup:hidden", onHidden);
      timer = setTimeout(onHidden, POPUP_HIDE_FALLBACK_MS);
    });
  }

  // inside class CardScreen
  public playWinIfAny(winAmount: number) {
    if (winAmount <= 0) return;
    this.cards.forEach((c) => (c as any).playWinAnim?.(winAmount));
  }
  public async resolveWildsSecondFlip(idsShown: [number, number, number]) {
    const targets = lastTransformTargets; // [t1,t2,t3]; 0 means no change
    const flips: Promise<void>[] = [];

    for (let i = 0; i < 3; i++) {
      const isWild =
        idsShown[i] === WILD_SUPER_ID || idsShown[i] === WILD_CIVIL_ID;
      const targetId = targets[i];
      if (!isWild || !targetId) continue;

      const card = this.cards[i];
      flips.push(
        card.morphToId(targetId, {
          half: 0.15, // time to 90deg (speed-up)
          finishPunch: 1.08, // tiny zoom punch on settle
          ease: "power2.inOut",
        })
      );
    }

    if (flips.length) {
      await Promise.all(flips);
    }
  }
}

/** Example wiring (call this from your scene once assets are loaded)

// Build CardScreen with 3 SymbolSpec derived from server ids.
// See loadCards.ts for helpers to decode ids to SymbolSpec.
*/

export async function onPreloadStartGame(container: PIXI.Container) {
  // Build the screen once with any 3 placeholders
  const firstSpecs = specsFromRoundTuple([1, 2, 3, 0, false, []]); // temp
  const screen = new CardScreen(firstSpecs, { w: 300, h: 450 });

  // ✅ IMPORTANT: local space only; app.ts handles placement via refitCardsToSlot()
  screen.position.set(0, 0);

  container.addChild(screen);
  return screen;

  // They show as closed side by default
}
export type WinEvent = { amount: number };
export async function onSpinClicked(screen: CardScreen) {
  if (!screen) return;
  LogoAnim.allsuper = false;
  screen.emit?.("roundstart");
  LogoAnim.StartBut_disableFun?.();
  if (LogoAnim.bottomWin) {
    LogoAnim.bottomWin.visible = false;
  }

  let scenarioResults: ScenarioResult[] = [];
  let win = 0;

  try {
    let amount = 0;
    let amountVal = currencySymbol + Number(amount).toFixed(2);
    if (ShowCent) {
      if (Number(amount) < 1 && Number(amount) > 0) {
        amountVal = Number(amount) * 100 + "\u00A2";
        if (amountVal.length > 4) {
          amountVal = (Number(amount) * 100).toFixed(2) + "\u00A2";
        }
      }
    }
    LogoAnim.totalwinContainer?.setValue(`${amountVal}`);
    BottomBar.TotalWin.text = `${amountVal}`;

    const tuple = await getRoundMock();
    const specs = specsFromRoundTuple(tuple);
    const [id1, id2, id3, rawWin, nearFlag, scenarioResultsRaw = []] = tuple;
    scenarioResults = scenarioResultsRaw;
    const idsShown = [id1, id2, id3] as [number, number, number];
    win = rawWin ?? 0;
    const nearTowin = win > 0 ? true : (nearFlag ?? false);

    (screen as any).currentWin = win;
    await screen.nextRound(specs, {
      halfDuration: DEFAULT_HALF_DURATION_MS,
      stagger: DEFAULT_REVEAL_STAGGER_MS,
      finishPunch: 1.06,
      ease: "power2.inOut",
      nearTowin,
    });

    await screen.resolveWildsSecondFlip(idsShown);

    if (scenarioResults.length) {
      await screen.playWinSequence(scenarioResults);
    } else if (win > 0) {
      screen.playWinIfAny(win);
    } else {
      SoundManager.Lose_Sound.play()
      if (shouldEnableSpinButton()) {
        LogoAnim.StartBut_enableFun?.();
      }
    }
  } catch (error) {
    console.error("Spin failed", error);
    if (shouldEnableSpinButton()) {
      LogoAnim.StartBut_enableFun?.();
    }
    throw error;
  } finally {
    screen.emit?.("roundend", { win, scenarioResults });
  }
}

